import { useEffect, useState } from 'react';
import type { DepartmentSummary } from '@todo/shared';
import { ApiError } from '../api/client';
import { departmentApi } from '../api/department.api';
import { useAuth } from '../hooks/useAuth';

/**
 * Pick WHERE the work (or the person) belongs.
 *
 * One component serves both forms - New person and New task - so the list,
 * the ordering and the inline "New department" flow stay identical. Creation
 * is offered only to admins (the API enforces people.manage regardless);
 * everyone else just picks from what exists.
 */
interface DepartmentSelectProps {
  /** Shows the * and drops the "(optional)" hint. */
  required?: boolean;
  value: string | undefined;
  onChange: (departmentId: string | undefined) => void;
  error?: string;
  disabled?: boolean;
}

const NEW_SENTINEL = '__new__';

export function DepartmentSelect({ required = false, value, onChange, error, disabled }: DepartmentSelectProps) {
  const { user } = useAuth();
  const [departments, setDepartments] = useState<DepartmentSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newName, setNewName] = useState('');
  const [createBusy, setCreateBusy] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  const canCreate =
    user?.role.systemKey === 'ADMIN' || user?.role.systemKey === 'SUPER_ADMIN';

  useEffect(() => {
    let cancelled = false;
    departmentApi
      .list()
      // A row with a BLANK id (hand-inserted in Workbench) would render as
      // <option value=""> - indistinguishable from "No department", so
      // selecting it silently snaps back. Refuse to offer such rows.
      .then((rows) => { if (!cancelled) setDepartments(rows.filter((d) => d.id && d.id.trim() !== '')); })
      .catch(() => undefined)
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  async function createDepartment() {
    const name = newName.trim();
    if (!name) return;
    setCreateBusy(true);
    setCreateError(null);
    try {
      const dept = await departmentApi.create({ name });
      // Insert in place alphabetically and select it immediately.
      setDepartments((current) =>
        [...current, dept].sort((a, b) => a.name.localeCompare(b.name)),
      );
      onChange(dept.id);
      setCreating(false);
      setNewName('');
    } catch (err) {
      setCreateError(err instanceof ApiError ? err.message : 'Could not create the department');
    } finally {
      setCreateBusy(false);
    }
  }

  return (
    <div>
      <label htmlFor="department-select" className="block text-sm font-medium text-slate-700">
        Department{' '}
        {required
          ? <span className="ml-0.5 text-red-500">*</span>
          : <span className="ml-1 text-slate-400">(optional)</span>}
      </label>

      {creating ? (
        <div className="mt-1.5 space-y-2">
          <input
            autoFocus
            value={newName}
            onChange={(e) => { setNewName(e.target.value); setCreateError(null); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { e.preventDefault(); void createDepartment(); }
            }}
            placeholder="Department name"
            className={[
              'w-full rounded-lg border px-3 py-2 text-sm outline-none transition placeholder:text-slate-400 focus:ring-2',
              createError
                ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
                : 'border-slate-300 focus:border-brand-500 focus:ring-brand-100',
            ].join(' ')}
          />
          {createError && <p className="text-xs text-red-600">{createError}</p>}
          <div className="flex gap-2">
            <button type="button" disabled={createBusy || !newName.trim()}
              onClick={() => void createDepartment()}
              className="rounded-lg bg-brand-600 px-3 py-1.5 text-xs font-medium text-white transition hover:bg-brand-700 disabled:opacity-50">
              {createBusy ? 'Creating...' : 'Create department'}
            </button>
            <button type="button"
              onClick={() => { setCreating(false); setNewName(''); setCreateError(null); }}
              className="rounded-lg px-3 py-1.5 text-xs font-medium text-slate-600 transition hover:bg-slate-100">
              Back
            </button>
          </div>
        </div>
      ) : (
        <select
          id="department-select"
          value={value ?? ''}
          disabled={disabled || loading}
          onChange={(e) => {
            if (e.target.value === NEW_SENTINEL) {
              setCreating(true);
              return;
            }
            onChange(e.target.value || undefined);
          }}
          aria-invalid={error ? true : undefined}
          className={[
            'mt-1.5 w-full rounded-lg border bg-white px-3 py-2 text-sm outline-none transition focus:ring-2',
            'disabled:cursor-not-allowed disabled:bg-slate-50 disabled:text-slate-400',
            error
              ? 'border-red-300 focus:border-red-400 focus:ring-red-100'
              : 'border-slate-300 focus:border-brand-500 focus:ring-brand-100',
          ].join(' ')}
        >
          <option value="">{loading ? 'Loading departments...' : (required ? 'Choose a department' : 'No department')}</option>
          {departments.map((dept) => (
            <option key={dept.id} value={dept.id}>{dept.name}</option>
          ))}
          {canCreate && <option value={NEW_SENTINEL}>+ New department…</option>}
        </select>
      )}
      {error && <p className="mt-1 text-xs text-red-600">{error}</p>}
    </div>
  );
}
